<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { computed, onMounted, ref, useEventListener, usePaymentsClient, useUserInfo } from '#imports'

const { useGetDepositSuccessfulCount } = usePaymentsClient()

const depositSuccessfulCount = useGetDepositSuccessfulCount()
const { isLoggedIn, data: user } = useUserInfo()

// True once the page has scrolled past the top, so a brand can style the fixed nav
// differently at rest and while scrolling. Set after mount, never during SSR, so a
// page that reloads already scrolled cannot cause a hydration mismatch.
const navScrolled = ref(false)

function updateNavScrolled() {
    navScrolled.value = window.scrollY > 8
}

useEventListener('scroll', updateNavScrolled, { passive: true })
onMounted(updateNavScrolled)

const showAccountCompletion = computed(() => {
    return isLoggedIn.value
        && user.value?.isValidated
        && depositSuccessfulCount.status.value === 'success'
        && depositSuccessfulCount.isFirstDeposit.value
})

export interface UILayoutProps {
    aside?: 'first' | 'last'
    flyer?: HTMLAttributes
    container?: HTMLAttributes
    fixedNav?: boolean
    hasSelfAssessment?: boolean
    bodyPadding?: boolean
    backgroundColor?: string
    termsPosition?: 'before-body' | 'after-body'
    /**
     * Reserves the account-completion card's height during SSR. Every input to
     * `true` must be resolvable during SSR, or the reservation shifts on its own.
     */
    reserveAccountCompletion?: boolean
}

withDefaults(defineProps<UILayoutProps>(), {
    aside: 'last',
    flyer: undefined,
    container: undefined,
    fixedNav: true,
    hasSelfAssessment: true,
    bodyPadding: true,
    termsPosition: 'before-body',
    reserveAccountCompletion: false
})
</script>

<template>
    <div class="layout">
        <div
            class="layout__container"
            :class="{ 'layout--has-selfassessment': hasSelfAssessment, 'layout--has-hero': !!$slots.hero }"
            v-bind="container"
            :style="{ backgroundColor: backgroundColor }"
        >
            <header v-if="$slots.header" data-test="site-header" class="layout__header">
                <div class="layout__nav" :class="{ 'layout__nav--fixed': fixedNav, 'layout__nav--scrolled': navScrolled }">
                    <slot name="header" />
                </div>

                <div v-if="fixedNav" class="layout__fixed-nav-spacer" />

                <section v-if="$slots.promotions" class="layout__promotions">
                    <slot name="promotions" />
                </section>

                <div
                    v-if="showAccountCompletion"
                    class="layout__completion"
                    :class="{ 'layout__completion--reserved': reserveAccountCompletion }"
                >
                    <slot name="accountCompletion">
                        <AccountCompletionSkeleton v-if="reserveAccountCompletion" />
                    </slot>
                </div>

                <div v-if="$slots.lobbyHero" class="layout__lobby-hero">
                    <slot name="lobbyHero" />
                </div>

                <div v-if="$slots.hero" class="layout__hero">
                    <slot name="hero" />
                </div>
            </header>

            <slot
                v-if="termsPosition === 'before-body'"
                name="terms"
            />

            <section
                class="layout__body"
                :class="{
                    'layout__body--aside-first': $slots.aside && aside === 'first',
                    'layout__body--no-aside': !$slots.aside,
                    'layout__body--no-padding': !bodyPadding,
                }"
            >
                <main class="layout__main">
                    <h1 v-if="$slots.title" class="layout__title">
                        <slot name="title" />
                    </h1>

                    <p v-if="$slots.lead" class="layout__lead">
                        <slot name="lead" />
                    </p>
                    <slot name="main" />
                </main>

                <aside v-if="$slots.aside" class="layout__aside">
                    <slot name="aside" />
                </aside>
            </section>

            <slot
                v-if="termsPosition === 'after-body'"
                name="terms"
            />
        </div>

        <footer v-if="$slots.footer" class="layout__footer">
            <slot name="footer" />
        </footer>

        <div v-if="$slots.flyer" class="layout__flyer" v-bind="flyer">
            <slot name="flyer" />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.layout {
    width: 100%;
    min-height: 100vh;
    margin: 0 auto;
    display: flex;
    flex-direction: column;

    @media (min-width: $screen-lg) {
        max-width: $container-lg;
    }

    @media (min-width: $screen-xl) {
        max-width: $container-xl;
    }

    &__container {
        flex: 1 0 auto;
        background-color: var(--layout-bg, $layout-bg);
    }

    &__promotions {
        padding: 0 15px;
        margin-bottom: 8px;

        @media (min-width: $screen-md) {
            padding: 0 30px;
        }
    }

    &__nav {
        background-color: var(--nav-bg, $nav-bg-color);

        &--fixed {
            background-image: $nav-bg-image;

            @media (min-width: $screen-md) {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                width: 100%;
                z-index: z(site-nav);
            }
        }
    }

    &__fixed-nav-spacer {
        @media (min-width: $screen-md) {
            margin-top: 60px;
            overflow: hidden;
        }

        .layout--has-selfassessment & {
            @media (min-width: $screen-md) {
                margin-top: 80px;
            }
        }
    }

    &__lead {
        margin-bottom: 0;
        font-weight: $text-regular;
        line-height: 1.4;

        @media (min-width: $screen-lg) {
            font-size: math.floor(($font-size-base * 1.15));
        }
    }

    &__completion {
        padding: 0 15px 15px;

        @media (min-width: $screen-md) {
            padding: 0 30px 30px;
        }

        @media (min-width: $screen-lg) {
            padding: 0;
        }

        // Measured heights of the wrapper with the card, per breakpoint (gap included).
        // `grid` stops the card's margin from collapsing through, and absorbs its slack.
        &--reserved {
            display: grid;
            min-height: 227px;

            @media (min-width: $screen-md) {
                min-height: 222px;
            }

            @media (min-width: $screen-lg) {
                min-height: 213px;
            }
        }
    }

    &__lobby-hero,
    &__hero {
        overflow: hidden;
    }

    &__hero {
        @media (min-width: $screen-xl) {
            overflow: visible;
        }
    }

    &__body,
    &__footer {
        padding: 15px;

        @media (min-width: $screen-md) {
            padding: 30px;
        }
    }

    &__body {
        grid-area: body;
        min-height: 100vh;
        display: grid;
        gap: 40px;
        grid-template-areas:
            'main'
            'aside';

        @media (min-width: $screen-md) {
            grid-template-areas: 'main aside';
            grid-template-columns: 9fr 4fr;
            grid-auto-flow: column;
        }

        @media (min-width: $screen-lg) {
            min-height: 70vh;
            grid-template-columns: 9fr 3fr;
        }

        &--aside-first {
            grid-template-areas:
                'aside'
                'main';

            @media (min-width: $screen-md) {
                grid-template-areas: 'aside main';
                grid-template-columns: 3fr 9fr;
            }

            @media (min-width: $screen-lg) {
                grid-template-columns: 4fr 9fr;
            }
        }

        &--no-aside {
            grid-template-areas: 'main';
            grid-template-columns: 100%;
        }

        &--no-padding {
            padding-top: 0 !important;
        }
    }

    &__main {
        grid-area: main;

        h1:first-child {
            margin-top: 0;
        }
    }

    &__aside {
        grid-area: aside;
    }

    &__footer {
        grid-area: footer;
        display: grid;
        gap: 40px;
    }

    &__flyer {
        position: 'fixed';
        bottom: 'auto';
        right: 'auto';
        left: 'auto';
        top: 'auto';
    }

    &--has-hero .offer-terms + &__body {
        padding-top: 10px;

        @include tablet(md) {
            padding-top: 20px;
        }

        @include desktop {
            padding-top: 30px;
        }
    }
}

.simple-layout {
    background-color: var(--layout-bg, $body-bg);

    &--full {
        .layout {
            max-width: unset;

            .layout__body {
                padding: 0;
            }
        }
    }
}
</style>
